/**
 * Coups de pouce — Attendu 903 « Traduire un problème en une seule expression ».
 * Trois cartes, six pages, quatre fiches, six generator_key.
 *
 * ─── Forme, reprise des revues 900 à 902 ───────────────────────────────────
 *   `court`  → l'idée-clé, une phrase (rendue en gras avec 💡)
 *   `lignes` → DEUX étapes, numérotées par la sheet
 *   `svg`    → le schéma seul, sans texte de règle
 * Seuls `court` et `lignes` sont lus à voix haute par CoupDePouceSheet : le
 * schéma est MUET, il garde son `aria-label` pour les lecteurs d'écran.
 *
 * ─── Ce qui est partagé, et pourquoi ───────────────────────────────────────
 * Les trois cartes ne demandent que DEUX gestes de traduction :
 *   - « une part d'abord, le nombre de parts ensuite » — c'est le même geste
 *     pour le salaire (h × (s − r)) et pour les livres (n × (p1 + p2)), au
 *     signe près. PAGE_UNITE le porte, et sert donc les deux cartes.
 *   - « un produit par sorte » — propre à la somme de produits.
 * Le troisième temps, remonter au prix manquant, ne concerne que les trous de
 * la carte rebours : PAGE_REBOURS n'apparaît que là.
 * PAGE_ELEVE est en page 2 de la fiche « rebours choix » (l'exemple concret du
 * geste) ET en page 1 de la fiche « rebours calcul » (la première étape de la
 * chaîne, dont PAGE_REBOURS est la suite) : la barre du total y est déjà
 * dessinée, la page suivante la reprend telle quelle.
 * Les pages communes le sont PAR IDENTITÉ d'objet — un seul littéral, référencé
 * deux fois. Les `generator_key` étant distincts, aucun routage par `sousType`.
 *
 * ─── Aucune fiche n'est du pur rappel ──────────────────────────────────────
 * Même règle acquise, il reste à chaque fois à décider CE QUI se répète dans
 * l'énoncé qu'on a sous les yeux, et dans quel ordre l'écrire. Les six clés ont
 * donc une entrée.
 *
 * ─── Les valeurs restent HORS des plages de tirage ─────────────────────────
 * Sinon une fiche servirait la réponse d'un exercice ouvert. Justifié générateur
 * en main, ET vérifié par tirage (leçon de la revue 902, où trois collisions
 * avaient survécu à la vérification analytique) :
 *   - salaire : la fiche montre 30 h, 20 € et 5 €. Le générateur tire
 *     h ∈ {140…160}, s ∈ [12..16] et une retenue DÉCIMALE de 1,50 € à 3,50 € ;
 *   - rebours : la fiche montre 12 élèves, 5 €, 3 € et 4 €. Le générateur tire
 *     n ∈ {25, 26, 28, 29, 30}, p1 ∈ [9,20..14,80] (toujours 2 décimales),
 *     p2 ∈ [18..26] et un prix manquant dans {20, 22, 24, 25, 26, 28} ;
 *   - somme de produits : la fiche montre 2 × 50 et 3 × 20. La branche entière
 *     tire n ∈ [4..12] (2 et 3 sont dessous) et p ∈ [8..25] (50 est au-dessus) ;
 *     la branche décimale tire bien n ∈ [2..6], mais toujours FACE À un volume
 *     décimal de 1,05 à 3,95 : « 2 × 50 » et « 3 × 20 » y sont inatteignables.
 * Les objets sont neufs eux aussi : paquets, palettes et briques n'apparaissent
 * dans aucun contexte du générateur (sacs, caisses, boîtes, zones, livres,
 * manuels).
 *
 * ⚠ Géométrie : les six figures passent une sonde qui reconstitue la boîte de
 * chaque <text> et vérifie qu'elle tient dans le viewBox et ne recouvre ni le
 * bouton « Revoir » ni un autre texte. Toute retouche est à re-mesurer.
 */

/**
 * Étape d'apparition : le groupe reste transparent jusqu'à `p` (fraction du
 * cycle), monte en 3 % puis GÈLE. Tous les groupes d'une figure partagent le
 * même `begin`, donc un clic sur le bouton rejoue toute la cascade.
 */
const apparition = (p, btn, duree = '7.6s') =>
    `<animate attributeName="opacity" values="0;0;1;1" ` +
    `keyTimes="0;${p.toFixed(2)};${(p + 0.03).toFixed(2)};1" ` +
    `dur="${duree}" begin="0s;${btn}.click" fill="freeze"/>`;

/** Groupe animé. opacity="1" en attribut = repli si SMIL est indisponible. */
const etape = (p, btn, contenu) =>
    `<g class="anim" opacity="1">${apparition(p, btn)}${contenu}</g>`;

const bouton = (id, x, y) =>
    `<g id="${id}" style="cursor:pointer">` +
    `<title>Rejouer l'animation</title>` +
    `<rect x="${x}" y="${y}" width="76" height="21" rx="10.5" fill="#EFF6FF" stroke="#1E3A8A" stroke-width="1.2"/>` +
    `<text x="${x + 38}" y="${y + 15}" text-anchor="middle" font-size="11" font-weight="bold" fill="#1E3A8A">↻ Revoir</text>` +
    `</g>`;

// Le seul CSS restant : respecter prefers-reduced-motion. En cas d'échec de la
// règle, l'élève voit l'animation, jamais une figure vide.
const STYLE_REDUCED = '<style>@media (prefers-reduced-motion: reduce) { .anim { opacity: 1 !important; } }</style>';

const BLEU = '#1E3A8A', ORANGE = '#f97316', ARDOISE = '#334155', ENCRE = '#0f172a';
const GRIS = '#94a3b8', VERT = '#16a34a', ROUGE = '#dc2626';

/** Filet de séparation entre les deux scènes d'une même figure. */
const filet = (y) => `<line x1="14" y1="${y}" x2="286" y2="${y}" stroke="#e2e8f0" stroke-width="1.2"/>`;

const titre = (txt) => `<text x="14" y="19" font-size="13" font-weight="bold" fill="${ENCRE}">${txt}</text>`;

const coche = (x, y) => `<text x="${x}" y="${y}" font-size="15" font-weight="bold" fill="${VERT}">✓</text>`;
const croix = (x, y) => `<text x="${x}" y="${y}" font-size="15" font-weight="bold" fill="${ROUGE}">✗</text>`;

/** Flèche verticale — le report d'un résultat vers l'étape suivante. */
const flecheV = (x, y1, y2, couleur = ORANGE) =>
    `<line x1="${x}" y1="${y1}" x2="${x}" y2="${y2 - 6}" stroke="${couleur}" stroke-width="1.6"/>` +
    `<path d="M ${x} ${y2} L ${x - 4} ${y2 - 7} L ${x + 4} ${y2 - 7} Z" fill="${couleur}"/>`;

/** Cartouche de fond d'une étiquette d'étape. */
const bande = (y, h = 34) =>
    `<rect x="14" y="${y}" width="272" height="${h}" rx="7" fill="#f8fafc" stroke="#cbd5e1" stroke-width="1.2"/>`;

/** Un paquet identique aux autres : le contenu qui se répète. */
const paquet = (x, y) =>
    `<rect x="${x}" y="${y}" width="56" height="40" rx="7" fill="#f8fafc" stroke="${BLEU}" stroke-width="1.2"/>` +
    `<text x="${x + 28}" y="${y + 17}" text-anchor="middle" font-size="11" font-weight="bold" fill="${ENCRE}">6 €</text>` +
    `<text x="${x + 28}" y="${y + 32}" text-anchor="middle" font-size="11" font-weight="bold" fill="${ORANGE}">+ 2 €</text>`;

// ═══════════════════════════════════════════════════════════
// Carte « salaire » et carte « rebours » — le même geste
// ═══════════════════════════════════════════════════════════

const BTN_A = 'cp903rejouerA', BTN_B = 'cp903rejouerB';

/**
 * Quatre paquets identiques, puis les deux écritures. La scène du bas n'est pas
 * décorative : c'est elle qui montre que dans `4 × 6 + 2`, le 2 € a disparu de
 * trois paquets sur quatre — la misconception commune aux deux cartes.
 */
const SVG_UNITE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 196" width="290" height="190" role="img"
     aria-label="Quatre paquets identiques, contenant chacun 6 euros et 2 euros. En dessous, deux écritures. La bonne : 4 fois parenthèse 6 plus 2, égale 32, coche verte, car les 2 euros reviennent dans chaque paquet. La mauvaise : 4 fois 6 plus 2, égale 26, croix rouge, car les 2 euros ne sont alors comptés qu'une seule fois. Un bouton Revoir rejoue l'animation.">
  ${STYLE_REDUCED}
  ${titre('Ce qui se répète')}
  ${bouton(BTN_A, 210, 4)}
  ${etape(0.05, BTN_A, paquet(14, 34))}
  ${etape(0.12, BTN_A, paquet(84, 34))}
  ${etape(0.19, BTN_A, paquet(154, 34))}
  ${etape(0.26, BTN_A, paquet(224, 34))}
  ${etape(0.34, BTN_A,
    `<text x="14" y="90" font-size="10" fill="${GRIS}">4 paquets, et toujours le même contenu</text>`)}
  ${etape(0.42, BTN_A, filet(102))}
  ${etape(0.50, BTN_A,
    `<text x="14" y="126" font-size="13.5" font-weight="bold" fill="${ENCRE}">4 × (6 + 2) = 32</text>` + coche(152, 127))}
  ${etape(0.62, BTN_A,
    `<text x="14" y="143" font-size="9.5" font-weight="bold" fill="${ORANGE}">les 2 € reviennent dans chaque paquet</text>`)}
  ${etape(0.76, BTN_A,
    `<text x="14" y="172" font-size="13.5" font-weight="bold" fill="${ROUGE}">4 × 6 + 2 = 26</text>` + croix(148, 173))}
  ${etape(0.88, BTN_A,
    `<text x="14" y="189" font-size="9.5" fill="${GRIS}">ici les 2 € ne sont comptés qu'une seule fois</text>`)}
</svg>`.trim();

/**
 * La variante SOUSTRACTIVE du même geste. L'erreur montrée (595 €) est celle du
 * piège n° 1 du générateur : la retenue prélevée une seule fois sur le total.
 */
const SVG_SALAIRE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 192" width="290" height="186" role="img"
     aria-label="Le salaire d'une heure. Une heure est payée 20 euros, on en retient 5 euros, il reste donc 15 euros par heure. Pour 30 heures : 30 fois parenthèse 20 moins 5, égale 450 euros, coche verte. L'erreur à éviter : 30 fois 20 moins 5, égale 595 euros, croix rouge, car la retenue ne porterait alors que sur le total. Un bouton Revoir rejoue l'animation.">
  ${STYLE_REDUCED}
  ${titre('Sur chaque heure')}
  ${bouton(BTN_B, 210, 4)}
  ${etape(0.05, BTN_B, bande(32) +
    `<text x="26" y="54" font-size="12" font-weight="bold" fill="${ENCRE}">1 heure payée 20 €</text>` +
    `<text x="196" y="54" font-size="12" font-weight="bold" fill="${ROUGE}">− 5 €</text>`)}
  ${etape(0.24, BTN_B, flecheV(40, 70, 88))}
  ${etape(0.34, BTN_B, bande(90) +
    `<text x="26" y="112" font-size="12" font-weight="bold" fill="${VERT}">il reste 15 € par heure</text>`)}
  ${etape(0.52, BTN_B,
    `<text x="14" y="150" font-size="13.5" font-weight="bold" fill="${ENCRE}">30 × (20 − 5) = 450 €</text>` + coche(190, 151))}
  ${etape(0.70, BTN_B,
    `<text x="14" y="174" font-size="13.5" font-weight="bold" fill="${ROUGE}">30 × 20 − 5 = 595 €</text>` + croix(178, 175))}
  ${etape(0.86, BTN_B,
    `<text x="14" y="188" font-size="9.5" fill="${GRIS}">la retenue ne porterait que sur le total</text>`)}
</svg>`.trim();

const PAGE_UNITE = {
    titre: 'Ce qui se répète va dans la parenthèse',
    court: 'Je cherche ce que vaut une seule part, puis je multiplie par le nombre de parts.',
    lignes: [
        'Tout ce qui revient à **chaque** part se regroupe entre parenthèses.',
        "Sans parenthèses, ce qui s'ajoute ou se retire ne compte qu'**une fois** en tout.",
    ],
    svg: SVG_UNITE,
};

const PAGE_SALAIRE = {
    titre: 'Un exemple : le salaire',
    court: 'La retenue est prélevée sur chaque heure, pas une seule fois sur le total.',
    lignes: [
        '20 € pour une heure, dont 5 € retenus : il reste **15 € par heure**.',
        'Pour 30 heures : 30 × (20 − 5) = **450 €**.',
    ],
    svg: SVG_SALAIRE,
};

/** Les deux formats de la carte demandent le même geste : fiche commune. */
const FICHE_SALAIRE = { pages: [PAGE_UNITE, PAGE_SALAIRE] };

// ═══════════════════════════════════════════════════════════
// Carte « rebours » — le lot par élève, puis le retour en arrière
// ═══════════════════════════════════════════════════════════

const BTN_C = 'cp903rejouerC', BTN_D = 'cp903rejouerD';

/** Barre du total, dont la part connue occupe les deux tiers gauches. */
const barreTotal = (y, wConnu, labelConnu, labelReste, couleurReste) =>
    `<rect x="14" y="${y}" width="272" height="26" rx="6" fill="#f1f5f9" stroke="#cbd5e1" stroke-width="1.2"/>` +
    `<rect x="14" y="${y}" width="${wConnu}" height="26" rx="6" fill="#dbeafe" stroke="${BLEU}" stroke-width="1.2"/>` +
    `<text x="${14 + wConnu / 2}" y="${y + 18}" text-anchor="middle" font-size="12" font-weight="bold" fill="${BLEU}">${labelConnu}</text>` +
    `<text x="${14 + wConnu + (272 - wConnu) / 2}" y="${y + 18}" text-anchor="middle" font-size="12" font-weight="bold" fill="${couleurReste}">${labelReste}</text>`;

const SVG_ELEVE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 186" width="290" height="180" role="img"
     aria-label="Ce que coûte un élève. Un cahier à 5 euros et une trousse à 3 euros : un élève coûte 8 euros. Pour 12 élèves : 12 fois parenthèse 5 plus 3, égale 96 euros, coche verte. C'est la dépense que l'on connaît. En dessous, une barre représente le total dépensé, 144 euros : les 96 euros connus en occupent la partie gauche, le reste est marqué d'un point d'interrogation. Un bouton Revoir rejoue l'animation.">
  ${STYLE_REDUCED}
  ${titre('Ce que coûte un élève')}
  ${bouton(BTN_C, 210, 4)}
  ${etape(0.05, BTN_C, bande(32) +
    `<text x="26" y="54" font-size="11.5" fill="${ARDOISE}">un cahier <tspan font-weight="bold" fill="${ENCRE}">5 €</tspan> et une trousse <tspan font-weight="bold" fill="${ENCRE}">3 €</tspan></text>`)}
  ${etape(0.22, BTN_C, flecheV(150, 70, 88))}
  ${etape(0.32, BTN_C,
    `<text x="14" y="106" font-size="13.5" font-weight="bold" fill="${ENCRE}">12 × (5 + 3) = 96 €</text>` + coche(172, 107))}
  ${etape(0.48, BTN_C,
    `<text x="14" y="123" font-size="9.5" font-weight="bold" fill="${ORANGE}">la dépense que je connais</text>`)}
  ${etape(0.64, BTN_C, barreTotal(136, 181, '96 €', '?', GRIS))}
  ${etape(0.84, BTN_C,
    `<text x="14" y="180" font-size="10" fill="${GRIS}">le collège a dépensé 144 € en tout</text>`)}
</svg>`.trim();

/** Douze pastilles : le reste ne va pas à un élève, il se partage entre tous. */
const pastilles = (y) => Array.from({ length: 12 }, (unused, i) =>
    `<circle cx="${24 + i * 22}" cy="${y}" r="6" fill="#dcfce7" stroke="${VERT}" stroke-width="1.2"/>`).join('');

const SVG_REBOURS = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 200" width="290" height="194" role="img"
     aria-label="Remonter au prix manquant. La barre du total, 144 euros, dont 96 euros sont déjà connus : il reste 48 euros. Le calcul s'écrit 144 moins 96 égale 48 euros. Ce reste concerne les douze élèves, représentés par douze pastilles : 48 divisé par 12 égale 4 euros, coche verte. Un bouton Revoir rejoue l'animation.">
  ${STYLE_REDUCED}
  ${titre('Retirer, puis partager')}
  ${bouton(BTN_D, 210, 4)}
  ${etape(0.05, BTN_D, barreTotal(32, 181, '96 €', '48 €', ORANGE))}
  ${etape(0.24, BTN_D,
    `<text x="14" y="82" font-size="13.5" font-weight="bold" fill="${ENCRE}">144 − 96 = <tspan fill="${ORANGE}">48 €</tspan></text>`)}
  ${etape(0.38, BTN_D, filet(96))}
  ${etape(0.48, BTN_D, pastilles(120))}
  ${etape(0.64, BTN_D,
    `<text x="14" y="150" font-size="10" fill="${GRIS}">les 48 € sont pour les 12 élèves, pas pour un seul</text>`)}
  ${etape(0.80, BTN_D,
    `<text x="14" y="180" font-size="13.5" font-weight="bold" fill="${VERT}">48 ÷ 12 = 4 €</text>` + coche(136, 181))}
</svg>`.trim();

const PAGE_ELEVE = {
    titre: 'Un exemple : le lot par élève',
    court: "Je calcule ce que coûte un seul élève, puis je multiplie par le nombre d'élèves.",
    lignes: [
        'Un cahier à 5 € et une trousse à 3 € : un élève coûte **8 €**.',
        'Pour 12 élèves : 12 × (5 + 3) = **96 €**.',
    ],
    svg: SVG_ELEVE,
};

const PAGE_REBOURS = {
    titre: 'Retirer, puis partager',
    court: 'Je retire du total la dépense que je connais déjà, puis je partage ce qui reste.',
    lignes: [
        'Total 144 €, dépense connue 96 € : il reste **48 €**.',
        'Ce reste concerne les 12 élèves : 48 ÷ 12 = **4 €**.',
    ],
    svg: SVG_REBOURS,
};

/** Le QCM s'arrête à la traduction : la règle, puis son exemple. */
const FICHE_REBOURS_CHOIX = { pages: [PAGE_UNITE, PAGE_ELEVE] };

/** Les trous enchaînent les trois étapes : l'exemple, puis le retour en arrière. */
const FICHE_REBOURS_CALCUL = { pages: [PAGE_ELEVE, PAGE_REBOURS] };

// ═══════════════════════════════════════════════════════════
// Carte « somme de produits » — un produit par sorte
// ═══════════════════════════════════════════════════════════

const BTN_E = 'cp903rejouerE', BTN_F = 'cp903rejouerF';

/** Une sorte : sa description à gauche, son produit à droite. */
const bandeSorte = (y, gauche, droite) =>
    bande(y) +
    `<text x="26" y="${y + 21}" font-size="10.5" fill="${BLEU}">${gauche}</text>` +
    `<text x="176" y="${y + 22}" font-size="12.5" font-weight="bold" fill="${ORANGE}">${droite}</text>`;

const SVG_SORTES = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 178" width="290" height="172" role="img"
     aria-label="Deux sortes de paquets, chacune avec son propre produit. Deux palettes de 50 briques : 2 fois 50 égale 100. Trois palettes de 20 briques : 3 fois 20 égale 60. On additionne ensuite les deux produits : 100 plus 60 égale 160 briques, coche verte. Chaque sorte garde son propre produit. Un bouton Revoir rejoue l'animation.">
  ${STYLE_REDUCED}
  ${titre('Un produit par sorte')}
  ${bouton(BTN_E, 210, 4)}
  ${etape(0.05, BTN_E, bandeSorte(32, '2 palettes de 50 briques', '2 × 50 = 100'))}
  ${etape(0.26, BTN_E, bandeSorte(76, '3 palettes de 20 briques', '3 × 20 = 60'))}
  ${etape(0.50, BTN_E, flecheV(150, 114, 132))}
  ${etape(0.62, BTN_E,
    `<text x="14" y="152" font-size="13.5" font-weight="bold" fill="${ENCRE}">100 + 60 = 160 briques</text>` + coche(196, 153))}
  ${etape(0.82, BTN_E,
    `<text x="14" y="170" font-size="9.5" fill="${GRIS}">chaque sorte garde son propre produit</text>`)}
</svg>`.trim();

const SVG_PIEGES = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 190" width="290" height="184" role="img"
     aria-label="Les deux pièges de la somme de produits, sur l'exemple des deux palettes de 50 briques et des trois palettes de 20 briques. Premier piège : 2 fois 20 plus 3 fois 50 égale 190, croix rouge, les nombres ont été croisés d'une sorte à l'autre. Deuxième piège : parenthèse 2 plus 3, fois parenthèse 50 plus 20, égale 350, croix rouge, tout a été multiplié ensemble. La seule écriture juste reste 2 fois 50 plus 3 fois 20, égale 160, coche verte. Un bouton Revoir rejoue l'animation.">
  ${STYLE_REDUCED}
  ${titre('Les deux pièges')}
  ${bouton(BTN_F, 210, 4)}
  ${etape(0.05, BTN_F,
    `<text x="14" y="50" font-size="13" font-weight="bold" fill="${ROUGE}">2 × 20 + 3 × 50 = 190</text>` + croix(186, 51))}
  ${etape(0.20, BTN_F,
    `<text x="14" y="67" font-size="9.5" fill="${GRIS}">les nombres ont été croisés</text>`)}
  ${etape(0.32, BTN_F, filet(84))}
  ${etape(0.42, BTN_F,
    `<text x="14" y="114" font-size="13" font-weight="bold" fill="${ROUGE}">(2 + 3) × (50 + 20) = 350</text>` + croix(210, 115))}
  ${etape(0.58, BTN_F,
    `<text x="14" y="131" font-size="9.5" fill="${GRIS}">tout a été multiplié ensemble</text>`)}
  ${etape(0.70, BTN_F, filet(148))}
  ${etape(0.82, BTN_F,
    `<text x="14" y="178" font-size="13.5" font-weight="bold" fill="${VERT}">2 × 50 + 3 × 20 = 160</text>` + coche(196, 179))}
</svg>`.trim();

const PAGE_SORTES = {
    titre: 'Un produit par sorte',
    court: 'Chaque sorte a son propre effectif et sa propre quantité.',
    lignes: [
        '2 palettes de 50 briques : 2 × 50 = **100**.',
        '3 palettes de 20 briques : 3 × 20 = **60**, donc 160 en tout.',
    ],
    svg: SVG_SORTES,
};

const PAGE_PIEGES = {
    titre: 'Les deux pièges',
    court: 'Croiser les nombres ou tout multiplier ensemble change le résultat.',
    lignes: [
        '2 × 20 + 3 × 50 = 190 : les nombres ont été **croisés**.',
        '(2 + 3) × (50 + 20) = 350 : tout a été **multiplié ensemble**.',
    ],
    svg: SVG_PIEGES,
};

/** QCM et trous demandent la même décomposition : fiche commune. */
const FICHE_SOMME = { pages: [PAGE_SORTES, PAGE_PIEGES] };

const CP_903 = {
    // Carte « salaire » : le QCM choisit l'expression, la saisie la calcule —
    // même geste de traduction, fiche entière partagée.
    salaire_qcm_unique:               FICHE_SALAIRE,
    salaire_texte:                    FICHE_SALAIRE,
    // Carte « somme de produits » : idem, les deux formats décomposent la même
    // somme (le QCM s'arrête à l'écriture, les trous la mènent au bout).
    somme_produits_qcm_unique:        FICHE_SOMME,
    somme_produits_texte_trou_saisie: FICHE_SOMME,
    // Carte « rebours » : PAGE_ELEVE est commune. Le QCM la reçoit comme exemple
    // du geste (page 2), les trous comme première étape de la chaîne (page 1),
    // celle dont PAGE_REBOURS reprend la barre.
    rebours_qcm_unique:               FICHE_REBOURS_CHOIX,
    rebours_texte_trou_saisie:        FICHE_REBOURS_CALCUL,
};

export default CP_903;
